import { DialogContent, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'

import { PlanValues } from 'api/elplan/elplan.types'
import React, { ReactElement, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import elplanStore from 'store/elplan/elplan'
import NewButton from 'ui/atoms/NewButton/NewButton'
import NewSwitch from 'ui/atoms/NewSwitch/NewSwitch'
import { Dialog } from 'ui/components/Dialog/Dialog'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import CustomDialogTitle from 'ui/components/SettingsModal/components/CustomDialogTitle/CustomDialogTitle'
import NewModalButtons from 'ui/molecules/ModalButtons/NewModalButtons'
import Datetime from 'utils/datetime/datetime'
import { getDayHourStarts, getDayQuarterStarts, getDayTimeStepLabels } from 'utils/datetime/dayTimeSteps'
import { isEmptyValue, parseDecimalInput } from 'utils/stringUtils'
import { useSnapshot } from 'valtio'
import styles from './SpotBidModal.module.less'
import { spotBidSaveValues } from './spotBidSaveValues.helper'

function getSpotBidHourStart(start: ISODateTime): ISODateTime {
  return Datetime.addMinutes(start, -new Datetime(start).minutes)
}

function fieldKey(hourKey: string, qIdx?: number): string {
  return qIdx !== undefined ? `${hourKey}:${qIdx}` : hourKey
}

type SpotBidModalProps = {
  onClose: () => void
  onSubmit: (planValues: PlanValues[]) => Promise<boolean>
  planValues: PlanValues[]
  elplanExistsWarningText?: string
  title?: string
  savingDisabled?: boolean
}

export default function SpotBidModal({
  planValues,
  onClose,
  onSubmit,
  title,
  elplanExistsWarningText,
  savingDisabled,
}: SpotBidModalProps): ReactElement {
  const ref = useRef(null)
  const elplanSnap = useSnapshot(elplanStore)
  const [expandedHourIdxs, setExpandedHourIdxs] = useState<number[]>([])
  const [inputMode, setInputMode] = useState<'hourly' | '15-mins'>('hourly')
  const savingRef = useRef(false)
  const [isSaving, setIsSaving] = useState(false)
  function handleClose(): void {
    if (!savingRef.current) onClose()
  }

  // Tracks fields that the user has left (blurred) at least once while empty.
  // Empty-field warnings are deferred until blur so they don't flash while the user is still typing.
  const [blurredEmptyFields, setBlurredEmptyFields] = useState<Set<string>>(new Set())

  const markEmptyFieldBlurred = useCallback((hourKey: string, isEmpty: boolean, qIdx?: number): void => {
    const key = fieldKey(hourKey, qIdx)
    setBlurredEmptyFields((prev) => {
      const next = new Set(prev)
      if (isEmpty) {
        next.add(key)
      } else {
        next.delete(key)
      }
      return next
    })
  }, [])

  const { t } = useTranslation()

  const hourGroups = useMemo(() => {
    const groups: Record<string, PlanValues[]> = {}
    planValues.forEach((q) => {
      const key = getSpotBidHourStart(q.start_time)
      if (!groups[key]) groups[key] = []
      groups[key].push(q)
    })

    Object.values(groups).forEach((arr) =>
      arr.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
    )
    return groups
  }, [planValues])

  const hourKeys = Object.keys(hourGroups).sort()

  const [quarterValues, setQuarterValues] = useState<Record<string, number[]>>(() => {
    const obj: Record<string, number[]> = {}
    Object.entries(hourGroups).forEach(([hourKey, quarters]) => {
      obj[hourKey] = quarters.map((q) => q.volume)
    })
    return obj
  })
  const [quarterStringValues, setQuarterStringValues] = useState<Record<string, string[]>>(() => {
    const obj: Record<string, string[]> = {}
    Object.entries(hourGroups).forEach(([hourKey, quarters]) => {
      obj[hourKey] = quarters.map((q) => q.volume.toString())
    })
    return obj
  })

  const [quarterHourValues, setQuarterHourValues] = useState<Record<string, number[]>>(() => {
    const obj: Record<string, number[]> = {}
    Object.entries(hourGroups).forEach(([hourKey, quarters]) => {
      obj[hourKey] = quarters.map((q) => q.volume)
    })
    return obj
  })
  const [quarterHourStringValues, setQuarterHourStringValues] = useState<Record<string, string[]>>(() => {
    const obj: Record<string, string[]> = {}
    Object.entries(hourGroups).forEach(([hourKey, quarters]) => {
      obj[hourKey] = quarters.map((q) => q.volume.toString())
    })
    return obj
  })
  const rows = useMemo(() => {
    const day = hourKeys[0] as ISODateTime | undefined
    const hourStarts = day ? getDayHourStarts(day) : []
    const quarterStarts = day ? getDayQuarterStarts(day) : []
    const hourLabels = getDayTimeStepLabels(hourStarts, 60)
    const quarterLabels = getDayTimeStepLabels(quarterStarts, 15)
    const hourLabelByStart = new Map(hourStarts.map((start, index) => [start, hourLabels[index]]))
    const quarterLabelByStart = new Map(quarterStarts.map((start, index) => [start, quarterLabels[index]]))

    return hourKeys.map((hourKey, i) => {
      const quarters = hourGroups[hourKey]
      const range = hourLabelByStart.get(hourKey as ISODateTime)
      const quarterValsRaw = quarterValues[hourKey] || quarters.map((q) => q.volume)
      const quarterVals = quarterValsRaw.map((v) => {
        if (v === undefined || v === null) return 0
        return Array.isArray(v) ? v[0] : v
      })
      const hourAvgForQuarterInput = (quarterVals.reduce((a, b) => a + b, 0) / quarterVals.length).toFixed(1)

      const hourValueForHourInput = quarterHourStringValues ? (quarterHourStringValues[hourKey]?.[0] ?? '') : '' //If the hour has been mixed with from 15-min view, this will look weird
      if (inputMode === 'hourly') {
        const hasEmptyHourValue = isEmptyValue(hourValueForHourInput) && blurredEmptyFields.has(fieldKey(hourKey))
        return (
          <div key={hourKey} className={styles.SpotBidModal_Row}>
            <div className={styles.SpotBidModal_Time}>
              <Typography variant="body2" fontWeight={600} component="span">
                {range}
              </Typography>
              {hasEmptyHourValue && (
                <InfoBanner text={t('There are empty input fields for this hour')} style="warning" variant="icon" />
              )}
            </div>
            <div className={styles.SpotBidModal_InputWrapper}>
              <TextField
                fullWidth
                disabled={isSaving}
                size="small"
                variant="outlined"
                value={hourValueForHourInput}
                onChange={(e) => {
                  const valStr = e.target.value
                  setQuarterHourStringValues((prev) => {
                    const arr = new Array(quarters.length).fill(valStr)
                    return { ...prev, [hourKey]: arr }
                  })
                }}
                onBlur={() => {
                  markEmptyFieldBlurred(hourKey, isEmptyValue(hourValueForHourInput))
                  if (hourValueForHourInput) {
                    const parsed = parseDecimalInput(hourValueForHourInput)
                    setQuarterHourValues((prev) => {
                      const arr = new Array(quarters.length).fill(isNaN(parsed) ? 0 : parsed)
                      return { ...prev, [hourKey]: arr }
                    })
                  }
                }}
                slotProps={{
                  htmlInput: {
                    inputMode: 'decimal',
                    className: styles.SpotBidModal_Input,
                  },
                }}
              />
            </div>
          </div>
        )
      }

      const hasEmptyQuartersInHour = quarters.some(
        (_, qIdx) =>
          isEmptyValue(quarterStringValues[hourKey]?.[qIdx]) && blurredEmptyFields.has(fieldKey(hourKey, qIdx))
      )

      return (
        <React.Fragment key={hourKey}>
          <div className={styles.SpotBidModal_Row}>
            <div className={styles.SpotBidModal_Time}>
              <Typography variant="body2" fontWeight={600} component="span">
                {range}
              </Typography>
              {hasEmptyQuartersInHour && (
                <InfoBanner text={t('There are empty input fields for this hour')} style="warning" variant="icon" />
              )}
            </div>
            <div className={styles.SpotBidModal_InputWrapper}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                disabled
                value={hourAvgForQuarterInput}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Show quarters" arrow>
                        <IconButton
                          size="small"
                          disabled={isSaving}
                          onClick={() => {
                            if (expandedHourIdxs.includes(i)) {
                              setExpandedHourIdxs(expandedHourIdxs.filter((idx) => idx !== i))
                            } else {
                              setExpandedHourIdxs([...expandedHourIdxs, i])
                            }
                          }}
                          aria-label="Show quarters"
                        >
                          <span className="fal fa-chart-pie-alt" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                slotProps={{
                  htmlInput: {
                    inputMode: 'decimal',
                    className: styles.SpotBidModal_Input,
                  },
                }}
              />
            </div>
          </div>
          {expandedHourIdxs.includes(i) && (
            <div className={styles.SpotBidModal_QuarterExpansion}>
              {quarters.map((q, qIdx) => (
                <div key={q.start_time} className={styles.SpotBidModal_QuarterRow}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    component="span"
                    className={styles.SpotBidModal_QuarterTime}
                  >
                    {quarterLabelByStart.get(Datetime.toISOString(q.start_time))}
                  </Typography>
                  <TextField
                    size="small"
                    disabled={isSaving}
                    variant="outlined"
                    value={
                      quarterStringValues[hourKey]?.[qIdx] === undefined ||
                      quarterStringValues[hourKey]?.[qIdx] === null
                        ? ''
                        : quarterStringValues[hourKey]?.[qIdx]
                    }
                    onChange={(e) => {
                      const valStr = e.target.value
                      setQuarterStringValues((prev) => {
                        const arr = prev[hourKey] ? [...prev[hourKey]] : quarters.map((q) => q.volume.toString())
                        arr[qIdx] = valStr
                        return { ...prev, [hourKey]: arr }
                      })
                    }}
                    onBlur={() => {
                      markEmptyFieldBlurred(hourKey, isEmptyValue(quarterStringValues[hourKey]?.[qIdx]), qIdx)
                      const valStr = quarterStringValues[hourKey]?.[qIdx] || ''
                      setQuarterValues((prev) => {
                        const arr = prev[hourKey] ? [...prev[hourKey]] : quarters.map((q) => q.volume)
                        if (valStr === '') {
                          arr[qIdx] = 0
                        } else {
                          const val = parseDecimalInput(valStr)
                          if (!isNaN(val)) {
                            arr[qIdx] = val
                          }
                        }
                        return { ...prev, [hourKey]: arr }
                      })
                    }}
                    inputProps={{
                      inputMode: 'decimal',
                      className: styles.SpotBidModal_Input,
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </React.Fragment>
      )
    })
  }, [
    hourKeys,
    hourGroups,
    expandedHourIdxs,
    quarterValues,
    inputMode,
    quarterHourStringValues,
    quarterStringValues,
    blurredEmptyFields,
    markEmptyFieldBlurred,
    isSaving,
    t,
  ])

  const hasEmptyFields = useMemo(() => {
    if (inputMode === 'hourly') {
      return hourKeys.some(
        (hourKey) => isEmptyValue(quarterHourStringValues[hourKey]?.[0]) && blurredEmptyFields.has(fieldKey(hourKey))
      )
    }
    return hourKeys.some((hourKey) =>
      hourGroups[hourKey].some(
        (_, qIdx) =>
          isEmptyValue(quarterStringValues[hourKey]?.[qIdx]) && blurredEmptyFields.has(fieldKey(hourKey, qIdx))
      )
    )
  }, [inputMode, quarterHourStringValues, quarterStringValues, hourKeys, hourGroups, blurredEmptyFields])

  const half = Math.ceil(rows.length / 2)

  return (
    <div className={styles.SpotBidModal}>
      <Dialog open={true} aria-labelledby="form-dialog-title" maxWidth="md" fullWidth container={ref.current}>
        <CustomDialogTitle title={title ?? t('Edit electricity plan')} handleClose={handleClose} />
        <DialogContent>
          <InfoBanner
            text={t(
              `Note that you should only edit bids using the same resolution for a day. If you create bids on 15-min intervals and later edit them using hourly intervals, the 15-min bids will be adjusted to match the hourly resolution (and vice versa). Always check your inputs before saving.`
            )}
            style="info"
          />
          <br />
          <div className={styles.SpotBidModal_TableWrapper}>
            <div className={styles.SpotBidModal_HeaderContainer}>
              <Typography variant="caption" className={styles.SpotBidModal_Header}>
                {t('Volume')} (MW)
              </Typography>
              <div className={styles.SpotBidModal_SwitchContainer}>
                <NewSwitch
                  disabled={isSaving}
                  items={[
                    { value: 'hourly', label: t('Hourly') },
                    { value: '15-mins', label: t('15-mins') },
                  ]}
                  value={inputMode}
                  onClick={(value) => {
                    setInputMode(value as 'hourly' | '15-mins')
                    if (value === 'hourly') {
                      setExpandedHourIdxs([])
                    }
                  }}
                />
                {inputMode === '15-mins' && (
                  <NewButton
                    disabled={isSaving}
                    variant="outlined"
                    icon="fal fa-chart-pie-alt"
                    label={
                      expandedHourIdxs.length === hourKeys.length
                        ? t('Collapse all quarters')
                        : t('Expand all quarters')
                    }
                    onClick={() => {
                      if (expandedHourIdxs.length === hourKeys.length) {
                        setExpandedHourIdxs([])
                      } else {
                        setExpandedHourIdxs(hourKeys.map((_, i) => i))
                      }
                    }}
                  />
                )}
              </div>
            </div>
            <Grid container spacing={2} wrap="nowrap">
              <Grid container size={{ xs: 12, md: 6 }} className={styles.SpotBidModal_Column}>
                {rows.slice(0, half)}
              </Grid>
              <Grid container size={{ xs: 12, md: 6 }} className={styles.SpotBidModal_Column}>
                {rows.slice(half)}
              </Grid>
            </Grid>
          </div>
          {hasEmptyFields && (
            <div className={styles.SpotBidModal_WarningContainer}>
              <InfoBanner
                text={t('There are empty fields in your input. These will be replaced with 0 when saving.')}
                style="warning"
                variant="textWithBackground"
              />
            </div>
          )}
          <div className={styles.SpotBidModal_Footer}>
            {inputMode === 'hourly' && (
              <div className={styles.SpotBidModal_InfoBannerContainer}>
                <InfoBanner
                  text={t('Even if you enter values per hour, the bid will be saved in quarter-hour resolution.')}
                  style="info"
                  variant="text"
                />
              </div>
            )}
            <div className={styles.SpotBidModal_ButtonsContainer}>
              <NewModalButtons
                isSubmitting={isSaving}
                disableSubmit={savingDisabled}
                submitLabel={inputMode === 'hourly' ? t('Save hourly bid') : t('Save 15-min bid')}
                onSubmit={async () => {
                  if (savingRef.current) return
                  savingRef.current = true
                  setIsSaving(true)
                  const newPlanValues = spotBidSaveValues({
                    inputMode,
                    hourKeys,
                    hourGroups,
                    quarterValues,
                    quarterStringValues,
                    quarterHourValues,
                    quarterHourStringValues,
                  })
                  let saved = false
                  try {
                    saved = await onSubmit(newPlanValues)
                  } finally {
                    savingRef.current = false
                    setIsSaving(false)
                  }
                  if (saved) onClose()
                }}
                onCancel={handleClose}
                infoText={elplanSnap.elplan.has_plan ? elplanExistsWarningText : undefined}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
